/* eslint-disable */
import {useEffect, useMemo, useState} from 'react';
import {
  ActivityIndicator,
  Dimensions,
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Pdf from 'react-native-pdf';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import {spacing, typography} from '../config/theme';

const {width: SCREEN_WIDTH, height: SCREEN_HEIGHT} = Dimensions.get('window');

// Receipt links arrive as a plain string or as {url}; accept both.
export const getReceiptUrl = value =>
  typeof value === 'string' ? value : value?.url || null;

// True when the link points at a PDF. Query strings (signed / Firebase links
// like ".../receipt.pdf?alt=media&token=...") and encoded paths are handled.
const looksLikePdf = url => {
  if (!url) return false;
  try {
    const path = decodeURIComponent(url.split('#')[0].split('?')[0]);
    return /\.pdf$/i.test(path);
  } catch (e) {
    return /\.pdf($|\?|#)/i.test(url);
  }
};

/**
 * Full-screen, in-app receipt viewer. Shows images (pinch to zoom) and PDFs
 * (pinch to zoom, scroll, page counter) without leaving the app.
 *
 * Usage: <ReceiptViewerModal url={receiptUrl} onClose={() => setReceiptUrl(null)} />
 * The modal is visible whenever `url` is set.
 */
const ReceiptViewerModal = ({url, onClose}) => {
  const {t} = useTranslation();
  const insets = useSafeAreaInsets();
  // 'image' | 'pdf'. Starts from the URL; flips to 'pdf' if an image fails to
  // decode (covers PDF links that don't end in .pdf).
  const [kind, setKind] = useState('image');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState({current: 1, total: 0});

  useEffect(() => {
    if (!url) return;
    setKind(looksLikePdf(url) ? 'pdf' : 'image');
    setLoading(true);
    setFailed(false);
    setPage({current: 1, total: 0});
  }, [url]);

  const pdfSource = useMemo(() => (url ? {uri: url, cache: true} : null), [url]);

  const handleImageError = () => {
    if (kind === 'image') {
      // Might be a PDF served without a .pdf extension: try the PDF viewer.
      setKind('pdf');
      setLoading(true);
    } else {
      setLoading(false);
      setFailed(true);
    }
  };

  const handlePdfError = () => {
    setLoading(false);
    setFailed(true);
  };

  return (
    <Modal
      visible={!!url}
      transparent={false}
      animationType="fade"
      statusBarTranslucent
      onRequestClose={onClose}>
      <View style={styles.container}>
        <View style={[styles.header, {paddingTop: insets.top + spacing.sm}]}>
          <View style={styles.titleBlock}>
            <Text style={styles.title}>{t('root.PumpSearch.ViewReceipt')}</Text>
            {kind === 'pdf' && page.total > 1 && !failed && (
              <Text style={styles.pageText}>
                {page.current} / {page.total}
              </Text>
            )}
          </View>
          <TouchableOpacity
            style={styles.closeButton}
            activeOpacity={0.7}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={onClose}>
            <Text style={styles.closeText}>✕</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.body}>
          {!!url && !failed && kind === 'image' && (
            <ScrollView
              style={styles.imageScroll}
              contentContainerStyle={styles.imageScrollContent}
              maximumZoomScale={4}
              minimumZoomScale={1}
              centerContent
              bouncesZoom
              showsVerticalScrollIndicator={false}
              showsHorizontalScrollIndicator={false}>
              <Image
                source={{uri: url}}
                style={styles.image}
                resizeMode="contain"
                onLoadEnd={() => setLoading(false)}
                onError={handleImageError}
              />
            </ScrollView>
          )}

          {!!pdfSource && !failed && kind === 'pdf' && (
            <Pdf
              source={pdfSource}
              style={styles.pdf}
              trustAllCerts={false}
              enablePaging={false}
              fitPolicy={0}
              onLoadComplete={numberOfPages => {
                setLoading(false);
                setPage(p => ({...p, total: numberOfPages}));
              }}
              onPageChanged={(current, total) => setPage({current, total})}
              onError={handlePdfError}
            />
          )}

          {loading && !failed && (
            <ActivityIndicator
              style={styles.loader}
              size="large"
              color="#FFFFFF"
            />
          )}

          {failed && (
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>
                Could not load the receipt. Check your connection and try again.
              </Text>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000000',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
  },
  titleBlock: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing.sm,
  },
  title: {
    ...typography.headlineSm,
    color: '#FFFFFF',
  },
  pageText: {
    ...typography.labelMd,
    color: 'rgba(255,255,255,0.7)',
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  closeText: {
    color: '#FFFFFF',
    fontSize: 18,
    lineHeight: 20,
  },
  body: {
    flex: 1,
  },
  imageScroll: {
    flex: 1,
  },
  imageScrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT * 0.8,
  },
  pdf: {
    flex: 1,
    width: SCREEN_WIDTH,
    backgroundColor: '#000000',
  },
  loader: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  errorBox: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
  },
  errorText: {
    ...typography.bodyMd,
    color: '#FFFFFF',
    textAlign: 'center',
  },
});

export default ReceiptViewerModal;